import React, { useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Notification, NotificationType } from '../types';
import { getRoddoTheme } from '../constants/theme';
import {
  HeartIcon,
  MessageSquareIcon,
  MegaphoneIcon,
  XIcon,
  FishMascotIcon,
  TrophyIcon,
  ShieldIcon,
  FlagIcon,
  MarketplaceIcon,
  CheckCircleIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  UserIcon,
} from './icons';

interface NotificationsPanelProps {
  show: boolean;
  notifications: Notification[];
  onClose: () => void;
  onMarkAllRead: () => void;
  onClearAll: () => void;
  onNotificationClick: (notification: Notification) => void;
  onViewAll: () => void;
  isDarkMode?: boolean;
}

const DEFAULT_COLLAPSED_COUNT = 5;
const APP_SHELL_ID = 'roddo-app-shell';
const HEADER_NOTIFICATIONS_BUTTON_ID = 'roddo-header-notifications-button';

const formatTimeAgo = (timestamp: number) => {
  const now = Date.now();
  const seconds = Math.floor((now - timestamp) / 1000);

  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
};

const getNotificationIcon = (type: NotificationType) => {
  const typeKey = String(type || '').toUpperCase();
  if (typeKey === 'CREW_POST' || typeKey === 'CREW_REPLY') {
    return <MessageSquareIcon className="h-6 w-6 text-[#9C8CFF]" />;
  }
  if (typeKey.includes('FRIEND') || typeKey.includes('CREW')) {
    return <UserIcon className="h-6 w-6 text-[#D97706] dark:text-[#FFB45C]" />;
  }

  switch (type) {
    case NotificationType.NEW_LIKE:
      return <HeartIcon className="h-6 w-6 fill-current text-[#E6535F] dark:text-[#FF7A86]" />;
    case NotificationType.NEW_COMMENT:
    case NotificationType.NEW_REPLY:
    case NotificationType.NEW_MENTION:
      return <MessageSquareIcon className="h-6 w-6 text-[#7c6ff7]" />;
    case NotificationType.NEW_FOLLOW:
      return <UserIcon className="h-6 w-6 text-[#D97706] dark:text-[#FFB45C]" />;
    case NotificationType.ADMIN_ANNOUNCEMENT:
      return <MegaphoneIcon className="h-6 w-6 text-[#f9d96f] drop-shadow-[0_1px_0_rgba(0,0,0,0.25)]" />;
    case NotificationType.FISHING_ALERT:
      return <FishMascotIcon className="h-6 w-6" />;
    case NotificationType.COMMUNITY_EVENT:
      return <TrophyIcon className="h-6 w-6 text-[#facc15]" />;
    case NotificationType.LICENSE_EXPIRING:
      return <ShieldIcon className="h-6 w-6 text-orange-400" />;
    case NotificationType.RESTRICTED_AREA:
      return <FlagIcon className="h-6 w-6 text-[#ef4444]" />;
    case NotificationType.NEW_OFFER:
      return <MarketplaceIcon className="h-6 w-6 text-[#10b981]" />;
    case NotificationType.OFFER_ACCEPTED:
      return <CheckCircleIcon className="h-6 w-6 text-green-500" />;
    case NotificationType.OFFER_DECLINED:
      return <XIcon className="h-6 w-6 text-[#ef4444]" />;
    case NotificationType.OFFER_COUNTERED:
      return <MarketplaceIcon className="h-6 w-6 text-[#3b82f6]" />;
    case NotificationType.TOURNAMENT_ANNOUNCEMENT:
      return <TrophyIcon className="h-6 w-6 text-[#fde047]" />;
    default:
      return null;
  }
};

const getNotificationDetail = (n: Notification) => {
  const actor = (n as any).actorName || n?.actor?.name;
  const title = (n as any).title as string | undefined;
  const preview = (n as any).preview as string | undefined;
  const typeKey = String((n as any).type || '').toUpperCase();

  if (typeKey.includes('FRIEND_REQUEST') || typeKey.includes('CREW_REQUEST')) {
    return n.body ? '' : actor ? `${actor} wants to join your crew.` : 'Someone wants to join your crew.';
  }

  if (typeKey.includes('FRIEND_ACCEPTED') || typeKey.includes('CREW_ACCEPTED')) {
    return n.body ? '' : actor ? `You and ${actor} are now crew.` : 'Crew confirmed.';
  }

  switch (n.type) {
    case NotificationType.NEW_LIKE: {
      const who = actor ? `${actor} ` : '';
      const what = title ? `“${title}”` : 'your post';
      return `${who}liked ${what}`;
    }
    case NotificationType.NEW_COMMENT:
    case NotificationType.NEW_REPLY: {
      const who = actor ? `${actor} ` : '';
      const what = title ? `on “${title}”` : 'on your post';
      const snippet = preview ? ` • “${preview}”` : '';
      return `${who}commented ${what}${snippet}`;
    }
    case NotificationType.NEW_MENTION:
      return actor ? `${actor} mentioned you` : 'You were mentioned';
    case NotificationType.NEW_FOLLOW:
      return actor ? `${actor} followed you` : 'Someone followed you';
    case NotificationType.ADMIN_ANNOUNCEMENT:
      return preview ? `“${preview}”` : 'Tap to read the announcement';
    case NotificationType.LICENSE_EXPIRING:
      return preview || 'Your license is nearing expiry';
    case NotificationType.NEW_OFFER:
      return preview || 'New offer on a bazaar item';
    case NotificationType.OFFER_ACCEPTED:
      return preview || 'Your offer was accepted';
    case NotificationType.OFFER_DECLINED:
      return preview || 'Your offer was declined';
    case NotificationType.OFFER_COUNTERED:
      return preview || 'Counter-offer received';
    case NotificationType.TOURNAMENT_ANNOUNCEMENT:
      return preview || 'New tournament update';
    default:
      return preview || title || '';
  }
};

type PanelPosition = {
  top: number;
  left: number;
  width: number;
};

const getFallbackPosition = (): PanelPosition => {
  if (typeof window === 'undefined') {
    return { top: 92, left: 16, width: 380 };
  }

  const viewportWidth = window.innerWidth;
  const horizontalInset = 16;
  const width = Math.min(380, Math.max(280, viewportWidth - horizontalInset * 2));
  return {
    top: 92,
    left: Math.max(horizontalInset, viewportWidth - horizontalInset - width),
    width,
  };
};

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);

const measurePanelPosition = (): PanelPosition => {
  if (typeof document === 'undefined' || typeof window === 'undefined') {
    return getFallbackPosition();
  }

  const shell = document.getElementById(APP_SHELL_ID);
  const anchor = document.getElementById(HEADER_NOTIFICATIONS_BUTTON_ID);

  if (!shell) return getFallbackPosition();

  const shellRect = shell.getBoundingClientRect();
  const shellWidth = Math.max(0, shellRect.width);
  const viewportPadding = 16;
  const width = Math.min(380, Math.max(280, shellWidth - 32));
  const minLeft = Math.max(viewportPadding, shellRect.left + 16);
  const maxLeft = Math.min(
    shellRect.right - width - 16,
    window.innerWidth - viewportPadding - width,
  );

  if (anchor) {
    const anchorRect = anchor.getBoundingClientRect();
    const desiredLeft = anchorRect.left - width + anchorRect.width - 10;

    return {
      top: anchorRect.bottom + 8,
      left: clamp(desiredLeft, minLeft, maxLeft),
      width,
    };
  }

  const fallbackLeft = shellWidth <= 640 ? shellRect.left + 16 : shellRect.right - 16 - width;

  return {
    top: shellRect.top + 56,
    left: clamp(fallbackLeft, minLeft, maxLeft),
    width,
  };
};

const NotificationsPanel: React.FC<NotificationsPanelProps> = ({
  show,
  notifications,
  onClose,
  onMarkAllRead,
  onClearAll,
  onNotificationClick,
  onViewAll,
  isDarkMode = false,
}) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [position, setPosition] = useState<PanelPosition>(() => getFallbackPosition());
  const theme = getRoddoTheme(!!isDarkMode);

  const sorted = useMemo(
    () => [...notifications].sort((a, b) => b.timestamp - a.timestamp),
    [notifications],
  );

  const hasMoreThanCollapsed = sorted.length > DEFAULT_COLLAPSED_COUNT;
  const visible = expanded ? sorted : sorted.slice(0, DEFAULT_COLLAPSED_COUNT);

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (!show) {
      setExpanded(false);
    }
  }, [show]);

  useLayoutEffect(() => {
    if (!mounted || !show) return;

    const updatePosition = () => {
      setPosition(measurePanelPosition());
    };

    updatePosition();

    window.addEventListener('resize', updatePosition);
    window.addEventListener('orientationchange', updatePosition);

    const shell = document.getElementById(APP_SHELL_ID);
    const resizeObserver =
      typeof ResizeObserver !== 'undefined' && shell
        ? new ResizeObserver(() => updatePosition())
        : null;

    if (resizeObserver && shell) resizeObserver.observe(shell);

    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('orientationchange', updatePosition);
      resizeObserver?.disconnect();
    };
  }, [mounted, show, isDarkMode]);

  if (!show || !mounted || typeof document === 'undefined') return null;

  const chipBtnStyle: React.CSSProperties = {
    background: theme.panelStrong,
    border: `1px solid ${theme.borderStrong}`,
    color: theme.text,
    boxShadow: isDarkMode
      ? '0 3px 10px rgba(0,0,0,0.14)'
      : '0 3px 0 0 rgba(184,182,232,0.90)',
    backdropFilter: 'blur(18px)',
    WebkitBackdropFilter: 'blur(18px)',
  };

  const wrapperStyle: React.CSSProperties = {
    position: 'fixed',
    top: `${position.top}px`,
    left: `${position.left}px`,
    width: `${position.width}px`,
    maxWidth: 'calc(100vw - 32px)',
    maxHeight: 'calc(100svh - var(--roddo-safe-bottom-gap, 100px) - 12px)',
  };

  const panelStyle: React.CSSProperties = {
    background: isDarkMode ? '#101827' : '#FFFCF7',
    border: `1px solid ${theme.borderStrong}`,
    color: theme.text,
    boxShadow: isDarkMode
      ? '0 8px 22px rgba(0,0,0,0.28)'
      : '0 10px 26px rgba(89,65,37,0.10)',
    backdropFilter: 'blur(18px)',
    WebkitBackdropFilter: 'blur(18px)',
    transform: 'translateZ(0)',
    willChange: 'transform, opacity',
  };

  const innerStyle: React.CSSProperties = {
    margin: 0,
    borderRadius: 0,
    border: 'none',
    background: 'transparent',
    boxShadow: 'none',
    transform: 'translateZ(0)',
    willChange: 'transform, opacity',
  };

  const listWrapStyle: React.CSSProperties = {
    WebkitOverflowScrolling: 'touch',
  };

  const overlay = (
    <div className="pointer-events-none fixed inset-0 z-[1100]" data-roddo-theme={isDarkMode ? 'dark' : 'light'}>
      <div
        className="absolute inset-0 pointer-events-auto"
        onClick={() => {
          setExpanded(false);
          onClose();
        }}
      />

      <div className="pointer-events-none animate-slide-down roddo-safe-popover" style={wrapperStyle}>
        <div className="pointer-events-auto relative overflow-hidden rounded-[18px]" style={panelStyle}>

          <div className="relative p-3.5" style={innerStyle}>
            <div className="mb-3 flex items-center justify-between">
              <div>
                <h3 className="text-[16px] font-extrabold tracking-tight" style={{ color: theme.text }}>
                  {t('notifications.title', 'Notifications')}
                </h3>
                <p className="text-[12px]" style={{ color: theme.textSoft }}>
                  {expanded
                    ? t('notifications.subtitleFull', 'Everything you missed.')
                    : t('notifications.subtitleShort', 'Quick pulses from your Roddo world.')}
                </p>
                <div className="mt-2 h-[2px] w-10 rounded-full bg-[#2F80ED] dark:bg-[#67D8E8]" />
              </div>

              <div className="flex items-center gap-2">
                {hasMoreThanCollapsed && (
                  <button
                    onClick={() => setExpanded((v) => !v)}
                    className="roddo-tactile-hit grid h-9 w-9 place-items-center rounded-full transition active:translate-y-[1px]"
                    style={chipBtnStyle}
                    title={expanded ? 'Collapse' : 'Expand'}
                    aria-label={expanded ? 'Collapse' : 'Expand'}
                  >
                    {expanded ? (
                      <ChevronUpIcon className="h-4 w-4" style={{ color: theme.text }} />
                    ) : (
                      <ChevronDownIcon className="h-4 w-4" style={{ color: theme.text }} />
                    )}
                  </button>
                )}

                <button
                  onClick={() => {
                    setExpanded(false);
                    onClose();
                  }}
                  className="roddo-tactile-hit grid h-9 w-9 place-items-center rounded-full transition active:translate-y-[1px]"
                  style={chipBtnStyle}
                  aria-label={t('common.close', 'Close')}
                >
                  <XIcon className="h-4 w-4" style={{ color: theme.text }} />
                </button>
              </div>
            </div>

            <div
              className={[
                'scrollbar-none space-y-2 overflow-y-auto rounded-xl p-1',
                expanded ? 'max-h-[min(60vh,520px)]' : 'max-h-[320px]',
              ].join(' ')}
              style={listWrapStyle}
            >
              {visible.length > 0 ? (
                visible.map((n) => {
                  const isClickable = !!n.link;
                  const Wrapper: any = isClickable ? 'button' : 'div';
                  const detail = getNotificationDetail(n);

                  const cardStyle: React.CSSProperties = {
                    border: `1px solid ${theme.borderStrong}`,
                    background: n.read ? theme.panel : theme.panelStrong,
                    color: theme.text,
                    boxShadow: n.read
                      ? isDarkMode
                        ? '0 4px 12px rgba(0,0,0,0.16)'
                        : '0 4px 12px rgba(89,65,37,0.06)'
                      : isDarkMode
                        ? '0 4px 12px rgba(0,0,0,0.16)'
                        : '0 4px 12px rgba(89,65,37,0.05)',
                    opacity: n.read ? 0.9 : 1,
                    transform: 'translateZ(0)',
                  };

                  return (
                    <Wrapper
                      key={n.id}
                      onClick={
                        isClickable
                          ? () => {
                              onNotificationClick(n);
                              if (!expanded) {
                                setExpanded(false);
                                onClose();
                              }
                            }
                          : undefined
                      }
                      className={[
                        'w-full text-left flex items-start gap-3 rounded-[12px] p-3 transition',
                        isClickable ? 'hover:-translate-y-0.5' : '',
                      ].join(' ')}
                      style={cardStyle}
                    >
                      <div className="mt-0.5 shrink-0">{getNotificationIcon(n.type)}</div>

                      <div className="min-w-0 flex-grow">
                        <p className="text-sm font-extrabold" style={{ color: theme.text }}>
                          {n.title || n.message || 'Notification'}
                        </p>

                        {(n.body || (!n.title && n.message)) && (
                          <p className="mt-0.5 line-clamp-2 text-[12px]" style={{ color: theme.textSoft }}>
                            {n.body || (n.title ? n.message : '')}
                          </p>
                        )}

                        {detail ? (
                          <p className="mt-0.5 line-clamp-2 text-[12px] leading-snug" style={{ color: theme.textSoft }}>
                            {detail}
                          </p>
                        ) : null}

                        <p className="mt-1 text-[11px]" style={{ color: theme.textSoft }}>
                          {formatTimeAgo(n.timestamp)}
                        </p>
                      </div>

                      {!n.read && (
                        <span className="mt-1 h-2.5 w-2.5 rounded-full bg-[#2F80ED] dark:bg-[#67D8E8]" />
                      )}
                    </Wrapper>
                  );
                })
              ) : (
                <p className="py-8 text-center text-sm" style={{ color: theme.textSoft }}>
                  {t('notifications.empty', 'You’re all caught up!')}
                </p>
              )}
            </div>

            {!expanded && hasMoreThanCollapsed && (
              <div className="mt-3 pt-3" style={{ borderTop: `1px solid ${theme.borderStrong}` }}>
                <button
                  onClick={() => setExpanded(true)}
                  className="w-full rounded-[11px] p-2.5 text-sm font-semibold transition active:translate-y-[1px]"
                  style={chipBtnStyle}
                >
                  {t('notifications.showMore', 'Show more')}
                </button>
              </div>
            )}

            {sorted.length > 0 && (
              <div className="mt-3 flex flex-col gap-2 pt-3" style={{ borderTop: `1px solid ${theme.borderStrong}` }}>
                <button
                  onClick={() => {
                    setExpanded(false);
                    onClose();
                    onViewAll();
                  }}
                  className="w-full rounded-[11px] p-2.5 text-sm font-semibold text-white transition-transform hover:-translate-y-0.5"
                  style={{ background: isDarkMode ? '#8A70F2' : '#2F80ED' }}
                >
                  {t('notifications.viewAll', 'View all notifications')}
                </button>

                <div className="flex gap-2">
                  <button
                    onClick={onMarkAllRead}
                    className="w-full rounded-[11px] p-2 text-xs font-semibold transition active:translate-y-[1px]"
                    style={chipBtnStyle}
                  >
                    {t('notifications.markAllRead', 'Mark all read')}
                  </button>

                  <button
                    onClick={onClearAll}
                    className="w-full rounded-[11px] border p-2 text-xs font-semibold transition active:translate-y-[1px]"
                    style={{
                      background: theme.panel,
                      border: `1px solid ${theme.borderStrong}`,
                      color: isDarkMode ? '#fca5a5' : '#b91c1c',
                      boxShadow: 'none',
                    }}
                  >
                    {t('notifications.clearAll', 'Clear all')}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(overlay, document.body);
};

export default NotificationsPanel;

if (typeof document !== 'undefined' && !document.getElementById('roddo-scrollbar-none-style')) {
  const style = document.createElement('style');
  style.id = 'roddo-scrollbar-none-style';
  style.innerHTML = `
    .scrollbar-none::-webkit-scrollbar {
      display: none;
    }
    .scrollbar-none {
      -ms-overflow-style: none;
      scrollbar-width: none;
    }
  `;
  document.head.appendChild(style);
}
